/**
 * History Page
 * Displays past conversations with search and sort options.
 * Follows the Projects page design pattern.
 */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { ArrowLeft, MessageSquare, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatHistory, useChatHistory } from '../components/ChatHistory';
import { ErrorState, LoadingState, PageBody, PageEmptyState, PageHeader, PageHero, PageToolbar } from '../components/layout';
import { useFeatureFlag, useFeatureFlagsReady } from '../contexts/featureFlagContext';
import { useLayoutStore } from '../lib/stores/LayoutStore';
export default function History() {
    const flagsReady = useFeatureFlagsReady();
    const redesignEnabled = useFeatureFlag('specra_redesign_history');
    if (flagsReady && redesignEnabled) {
        return /*#__PURE__*/ _jsx(HistoryRedesign, {});
    }
    return /*#__PURE__*/ _jsx(HistoryLegacy, {});
}
function HistoryRedesign() {
    const navigate = useNavigate();
    const [sortBy, setSortBy] = useState('recent');
    const [searchQuery, setSearchQuery] = useState('');
    const { isLoading, error, hasChats, refetch } = useChatHistory();
    const setShowHeader = useLayoutStore((state)=>state.setShowHeader);
    const setShowBreadcrumbs = useLayoutStore((state)=>state.setShowBreadcrumbs);
    useEffect(()=>{
        setShowHeader(false);
        setShowBreadcrumbs(false);
        return ()=>{
            setShowHeader(true);
            setShowBreadcrumbs(true);
        };
    }, [
        setShowHeader,
        setShowBreadcrumbs
    ]);
    const handleNewChat = ()=>navigate('/assistant');
    return /*#__PURE__*/ _jsxs(PageBody, {
        children: [
            /*#__PURE__*/ _jsx(PageHero, {
                title: "History",
                subtitle: "Review your past conversations"
            }),
            isLoading ? /*#__PURE__*/ _jsx(LoadingState, {
                rows: 3
            }) : error ? /*#__PURE__*/ _jsx(ErrorState, {
                message: "Failed to load chat history. Please try again.",
                onRetry: refetch
            }) : !hasChats ? /*#__PURE__*/ _jsx(PageEmptyState, {
                icon: /*#__PURE__*/ _jsx(MessageSquare, {}),
                title: "No conversations yet",
                description: "Start a new chat to begin your conversation history.",
                action: {
                    label: 'Start a new chat',
                    onClick: handleNewChat
                },
                size: "lg"
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(PageToolbar, {
                        children: [
                            /*#__PURE__*/ _jsx(PageToolbar.Search, {
                                value: searchQuery,
                                onChange: setSearchQuery,
                                placeholder: "Search conversations..."
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "flex items-center gap-2",
                                children: /*#__PURE__*/ _jsxs("select", {
                                    value: sortBy,
                                    onChange: (e)=>setSortBy(e.target.value),
                                    className: "px-3 py-2 border border-border rounded-lg bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-brand",
                                    children: [
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "recent",
                                            children: "Most Recent"
                                        }),
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "oldest",
                                            children: "Oldest First"
                                        }),
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "name",
                                            children: "A-Z"
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(PageToolbar.Spacer, {}),
                            /*#__PURE__*/ _jsx(PageToolbar.Actions, {
                                children: /*#__PURE__*/ _jsxs(SpecraButton, {
                                    onClick: handleNewChat,
                                    children: [
                                        /*#__PURE__*/ _jsx(Plus, {
                                            className: "w-4 h-4 mr-2"
                                        }),
                                        "New Chat"
                                    ]
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(ChatHistory, {
                        sortBy: sortBy,
                        searchQuery: searchQuery
                    })
                ]
            })
        ]
    });
}
function HistoryLegacy() {
    const navigate = useNavigate();
    const [sortBy, setSortBy] = useState('recent');
    const [searchQuery, setSearchQuery] = useState('');
    const { isLoading, error, hasChats, refetch } = useChatHistory();
    // Hide breadcrumbs on root page
    const setShowBreadcrumbs = useLayoutStore((state)=>state.setShowBreadcrumbs);
    useEffect(()=>{
        setShowBreadcrumbs(false);
        return ()=>setShowBreadcrumbs(true);
    }, [
        setShowBreadcrumbs
    ]);
    const handleNewChat = ()=>{
        navigate('/assistant');
    };
    // Loading state
    if (isLoading) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(PageHeader, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "pl-8",
                        children: [
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-xl font-semibold",
                                children: "History"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground",
                                children: "Review your past conversations"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(PageBody, {
                    children: /*#__PURE__*/ _jsx(LoadingState, {
                        rows: 3
                    })
                })
            ]
        });
    }
    // Error state
    if (error) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(PageHeader, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "pl-8",
                        children: [
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-xl font-semibold",
                                children: "History"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground",
                                children: "Review your past conversations"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(PageBody, {
                    children: /*#__PURE__*/ _jsx(ErrorState, {
                        message: "Failed to load chat history. Please try again.",
                        onRetry: refetch
                    })
                })
            ]
        });
    }
    // No chats at all - show empty state without toolbar
    if (!hasChats) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(PageHeader, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "pl-8",
                        children: [
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-xl font-semibold",
                                children: "History"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground",
                                children: "Review your past conversations"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(PageBody, {
                    children: /*#__PURE__*/ _jsx(PageEmptyState, {
                        icon: /*#__PURE__*/ _jsx(MessageSquare, {}),
                        title: "No conversations yet",
                        description: "Start a new chat to begin your conversation history.",
                        action: {
                            label: 'Start a new chat',
                            onClick: handleNewChat
                        },
                        size: "lg"
                    })
                })
            ]
        });
    }
    // Has chats - show toolbar and chat history
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(PageHeader, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "pl-8",
                    children: [
                        /*#__PURE__*/ _jsx("h2", {
                            className: "text-xl font-semibold",
                            children: "History"
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-muted-foreground",
                            children: "Review your past conversations"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(PageBody, {
                children: [
                    /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        onClick: ()=>navigate(-1),
                        className: "mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground",
                        children: [
                            /*#__PURE__*/ _jsx(ArrowLeft, {
                                className: "w-4 h-4"
                            }),
                            "Back"
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(PageToolbar, {
                        children: [
                            /*#__PURE__*/ _jsx(PageToolbar.Search, {
                                value: searchQuery,
                                onChange: setSearchQuery,
                                placeholder: "Search conversations..."
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "flex items-center gap-2",
                                children: /*#__PURE__*/ _jsxs("select", {
                                    value: sortBy,
                                    onChange: (e)=>setSortBy(e.target.value),
                                    className: "px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600 dark:focus:ring-indigo-500",
                                    children: [
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "recent",
                                            children: "Most Recent"
                                        }),
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "oldest",
                                            children: "Oldest First"
                                        }),
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "name",
                                            children: "A-Z"
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(PageToolbar.Spacer, {}),
                            /*#__PURE__*/ _jsx(PageToolbar.Actions, {
                                children: /*#__PURE__*/ _jsxs(SpecraButton, {
                                    onClick: handleNewChat,
                                    children: [
                                        /*#__PURE__*/ _jsx(Plus, {
                                            className: "w-4 h-4 mr-2"
                                        }),
                                        "New Chat"
                                    ]
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(ChatHistory, {
                        sortBy: sortBy,
                        searchQuery: searchQuery
                    })
                ]
            })
        ]
    });
}
